import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { NovelService, Novel, NovelCharacter } from '../../services/novelService';
import PageHeader from '../common/PageHeader';
import './CharacterEditor.css';

interface CharacterEditorProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const CharacterEditor: React.FC<CharacterEditorProps> = ({ user, onClose, onToggleSidebar }) => {
  const { novelId, characterIndex } = useParams<{ novelId: string; characterIndex: string }>();
  const navigate = useNavigate();
  
  const [novel, setNovel] = useState<Novel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [character, setCharacter] = useState<NovelCharacter>({ 
    name: '', 
    description: '', 
    role: 'other' 
  });
  const [syncStatus, setSyncStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [enhancing, setEnhancing] = useState(false);

  const index = characterIndex ? parseInt(characterIndex, 10) : 0;

  useEffect(() => {
    if (novelId) {
      loadNovel();
    }
  }, [novelId]);

  const loadNovel = async () => {
    if (!novelId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await NovelService.getNovel(novelId);
      setNovel(data);
      
      const characters = data.metadata?.characters || [];
      if (index >= 0 && index < characters.length) {
        setCharacter(characters[index]);
      }
    } catch (err) {
      console.error('Error loading novel:', err);
      setError('Failed to load novel');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/novel/${novelId}/metadata/characters`);
  };

  const handleSave = async () => {
    if (!novelId || !novel) return;

    try {
      setSyncStatus('saving');
      const characters = [...(novel.metadata?.characters || [])];
      characters[index] = character;
      
      const metadata = { ...novel.metadata, characters };
      const updated = await NovelService.updateNovelMetadata(novelId, novel.genre || '', metadata);
      setNovel(updated);
      setSyncStatus('saved');
      setError(null);
    } catch (err) {
      console.error('Error saving character:', err);
      setSyncStatus('error');
      setError('Failed to save character');
    }
  };

  const handleEnhance = async () => {
    if (!novelId || !character.name) return;

    setEnhancing(true);
    try {
      const enhanced = await NovelService.enhanceMetadataField(
        novelId,
        'character',
        character
      );
      setCharacter(enhanced);
    } catch (err: any) {
      console.error('Error enhancing character:', err);
      setError('Failed to enhance character');
    } finally {
      setEnhancing(false);
    }
  };

  const handleDelete = async () => {
    if (!novelId || !novel) return;
    if (!window.confirm('Are you sure you want to delete this character?')) return;

    try {
      setSyncStatus('saving');
      const characters = [...(novel.metadata?.characters || [])];
      characters.splice(index, 1);
      
      const metadata = { ...novel.metadata, characters };
      await NovelService.updateNovelMetadata(novelId, novel.genre || '', metadata);
      navigate(`/novel/${novelId}/metadata/characters`);
    } catch (err) {
      console.error('Error deleting character:', err);
      setError('Failed to delete character');
      setSyncStatus('error');
    }
  };

  const updateField = (field: keyof NovelCharacter, value: string) => {
    setCharacter({ ...character, [field]: value });
  };

  if (loading) {
    return (
      <div className="character-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
        />
        <div className="character-editor-loading">Loading character...</div>
      </div>
    );
  }

  if (error && !novel) {
    return (
      <div className="character-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
        />
        <div className="character-editor-error">
          {error || 'Novel not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="character-editor-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <div className="character-editor-header-center">
            <h1 className="page-header-center-title">
              {novel?.title} /character #{index + 1}
            </h1>
            <span className={`character-sync-status ${syncStatus}`}>
              {syncStatus === 'saving' && '⟳ Saving...'}
              {syncStatus === 'saved' && '✓ Saved'}
              {syncStatus === 'error' && '✕ Save failed'}
            </span>
          </div>
        }
        leftActions={
          <button 
            className="character-editor-back-btn"
            onClick={handleBack}
          >
            ← Back to Characters
          </button>
        }
        rightActions={
          <div className="character-editor-header-actions">
            <button 
              className="character-editor-enhance-btn"
              onClick={handleEnhance}
              disabled={!character.name || enhancing}
              title="Enhance with AI"
            >
              {enhancing ? '⟳' : '✨'} Enhance
            </button>
            <button 
              className="character-editor-save-btn"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        }
      />

      <div className="character-editor-content">
        <div className="character-editor-main">
          <div className="character-editor-form">
            <div className="character-editor-field">
              <label className="character-editor-label">/name</label>
              <input
                type="text"
                className="character-editor-input"
                value={character.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Character name..."
              />
            </div>

            <div className="character-editor-field">
              <label className="character-editor-label">/role</label>
              <select
                className="character-editor-select"
                value={character.role}
                onChange={(e) => updateField('role', e.target.value as any)}
              >
                <option value="protagonist">Protagonist</option>
                <option value="antagonist">Antagonist</option>
                <option value="supporting">Supporting</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="character-editor-field">
              <label className="character-editor-label">/description</label>
              <textarea
                className="character-editor-textarea"
                value={character.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Describe the character in detail (appearance, personality, background, motivations, relationships, etc.)..."
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="character-editor-error-message">
            {error}
          </div>
        )}

        <div className="character-editor-footer">
          <button
            className="character-editor-delete-btn"
            onClick={handleDelete}
          >
            Delete Character
          </button>
        </div>
      </div>
    </div>
  );
};

export default CharacterEditor;

